import React, { useState, useEffect, useRef } from 'react';
import { FileIcon, CheckCircle, Info, RefreshCw, Upload, X, Loader2, ImageIcon } from 'lucide-react';
import { useAppDemo } from '../../context/AppDemoContext';
import { toast } from 'react-hot-toast';

/**
 * AppAssetsSlide component for uploading various app assets needed for the complete app
 */
const AppAssetsSlide = ({ registerValidation }) => {
    const { appAssets, setAppAssets, organization, user } = useAppDemo();
    const [isLoading, setIsLoading] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(null);
    const fileInputRefs = useRef({});

    // Define all required app assets
    const requiredAssets = [
        { 
            id: 'icon-png', 
            name: 'App Icon (PNG)', 
            filename: 'icon.png', 
            type: 'image/png',
            description: 'Main app icon used for app stores and device home screens',
            placeholder: '🖼️' 
        },
        { 
            id: 'icon-svg', 
            name: 'App Icon (SVG)', 
            filename: 'icon.svg', 
            type: 'image/svg+xml',
            description: 'Vector version of the app icon for sharper rendering',
            placeholder: '🖼️' 
        },
        { 
            id: 'icon-dark-svg', 
            name: 'App Icon - Dark (SVG)', 
            filename: 'icon-dark.svg', 
            type: 'image/svg+xml',
            description: 'Dark mode version of the app icon',
            placeholder: '🖼️' 
        },
        { 
            id: 'logo-svg', 
            name: 'App Logo (SVG)', 
            filename: 'logo.svg', 
            type: 'image/svg+xml',
            description: 'Full logo used within the app interface',
            placeholder: '🖼️' 
        },
        { 
            id: 'logo-dark-svg', 
            name: 'App Logo - Dark (SVG)', 
            filename: 'logo-dark.svg', 
            type: 'image/svg+xml', 
            description: 'Dark mode version of the full app logo',
            placeholder: '🖼️' 
        },
        { 
            id: 'login-png', 
            name: 'Login Screen (PNG)', 
            filename: 'login.png', 
            type: 'image/png',
            description: 'Login screen background image',
            placeholder: '🖼️' 
        },
        { 
            id: 'login-dark-png', 
            name: 'Login Screen - Dark (PNG)', 
            filename: 'login-dark.png', 
            type: 'image/png',
            description: 'Dark mode login screen background image',
            placeholder: '🖼️' 
        },
        { 
            id: 'splash-png', 
            name: 'Splash Screen (PNG)', 
            filename: 'splash.png', 
            type: 'image/png',
            description: 'App splash/loading screen background',
            placeholder: '🖼️' 
        },
        { 
            id: 'splash-dark-png', 
            name: 'Splash Screen - Dark (PNG)', 
            filename: 'splash-dark.png', 
            type: 'image/png',
            description: 'Dark mode splash/loading screen background',
            placeholder: '🖼️' 
        },
        { 
            id: 'adaptive-icon-png', 
            name: 'Adaptive Icon (PNG)', 
            filename: 'adaptive-icon.png', 
            type: 'image/png',
            description: 'Android adaptive icon foreground layer',
            placeholder: '🖼️' 
        },
        { 
            id: 'adaptive-icon-bg-png', 
            name: 'Adaptive Icon Background (PNG)', 
            filename: 'adaptive-icon-bg.png', 
            type: 'image/png',
            description: 'Android adaptive icon background layer',
            placeholder: '🖼️' 
        },
        { 
            id: 'loading-gif', 
            name: 'Loading Animation (GIF)', 
            filename: 'loading.gif', 
            type: 'image/gif',
            description: 'Animation displayed during app loading screens',
            placeholder: '🖼️' 
        }
    ];

    // Initialize file input refs for each asset
    useEffect(() => {
        requiredAssets.forEach(asset => {
            if (!fileInputRefs.current[asset.id]) {
                fileInputRefs.current[asset.id] = React.createRef();
            }
        });
    }, []);

    // Debug: Display assets on component mount and every render to track state changes
    useEffect(() => {
        if (appAssets) {
            const assetCount = Object.keys(appAssets).length;
            console.log(`AppAssetsSlide render: Found ${assetCount} assets in context state:`, appAssets);
            if (assetCount > 0) {
                // List the assets that are available
                Object.keys(appAssets).forEach(assetId => {
                    console.log(`- Asset '${assetId}' is available at URL: ${appAssets[assetId].url.substring(0, 50)}...`);
                });
            }
        } else {
            console.log('AppAssetsSlide render: No assets found in context state');
        }
    }, [appAssets]);

    // Calculate completion percentage
    const getCompletionPercentage = () => {
        if (!appAssets) return 0;
        const uploadedCount = requiredAssets.filter(asset => 
            appAssets[asset.id] && appAssets[asset.id].url
        ).length;
        return Math.round((uploadedCount / requiredAssets.length) * 100);
    };

    // Handle file selection
    const handleFileSelect = (assetId) => {
        if (fileInputRefs.current[assetId]) {
            fileInputRefs.current[assetId].current.click();
        }
    };

    // Handle file upload
    const handleFileUpload = async (assetId, file) => {
        // Validate file type
        const asset = requiredAssets.find(a => a.id === assetId);
        if (!asset) return;

        if (!file.type.startsWith('image/')) {
            toast.error(`${asset.name} must be an image file`);
            return;
        }

        if (asset.type && !file.type.includes(asset.type.split('/')[1])) {
            toast.error(`${asset.name} must be a ${asset.type.split('/')[1].toUpperCase()} file`);
            return;
        }

        // Check file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File size exceeds 5MB limit');
            return;
        }

        setUploadingFile(assetId);

        try {
            // Create FormData and append file
            const formData = new FormData();
            formData.append('file', file);
            formData.append('assetType', asset.filename);
            formData.append('organizationId', organization?.id || 'default');
            formData.append('userId', user?.id || 'anonymous');
            
            // Make the API request
            const response = await fetch('/api/uploads/app-asset', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to upload ${asset.name}`);
            }

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || `Failed to upload ${asset.name}`);
            }

            // Update app assets state
            setAppAssets(prev => ({
                ...prev,
                [assetId]: {
                    url: data.fileUrl,
                    filename: asset.filename,
                    uploadedAt: new Date().toISOString()
                }
            }));

            toast.success(`${asset.name} uploaded successfully`);
        } catch (error) {
            console.error(`Error uploading ${asset.name}:`, error);
            toast.error(error.message || `Failed to upload ${asset.name}`);
        } finally {
            setUploadingFile(null);
        }
    };

    // Handle file input change
    const handleFileChange = (assetId, e) => {
        const file = e.target.files[0];
        if (!file) return;
        handleFileUpload(assetId, file);
    };

    // Validation logic for the slide
    useEffect(() => {
        // We'll consider the slide valid if at least one asset is uploaded
        // This could be adjusted based on which assets are truly required
        const isValid = appAssets && Object.keys(appAssets).length > 0;
        registerValidation('app-assets', isValid);
    }, [appAssets, registerValidation]);

    // Save assets to localStorage when they change
    useEffect(() => {
        if (appAssets && Object.keys(appAssets).length > 0) {
            console.log('Saving app assets to localStorage:', appAssets);
            
            // The AppDemoContext should handle this via its own useEffect,
            // but we'll add this as a safeguard
            try {
                // Get existing app demo state
                const savedState = localStorage.getItem('appDemoState');
                if (savedState) {
                    const parsedState = JSON.parse(savedState);
                    // Update with new app assets
                    const updatedState = {
                        ...parsedState,
                        appAssets
                    };
                    // Save back to localStorage
                    localStorage.setItem('appDemoState', JSON.stringify(updatedState));
                }
            } catch (error) {
                console.error('Error saving app assets to localStorage:', error);
            }
        }
    }, [appAssets]);

    // Debugging: log assets when component mounts or user changes
    useEffect(() => {
        console.log('AppAssetsSlide mounted or user changed, assets:', appAssets);
    }, [user?.id, appAssets]);

    return (
        <div className="slide-container">
            <div className="mb-6 flex items-center">
                <FileIcon className="w-6 h-6 text-yellow-300 mr-2" />
                <h2 className="text-2xl font-medium text-white">App Assets</h2>
            </div>
            
            <p className="text-white/70 mb-6">
                Upload the required assets for your app. These files will be used to build your app for different platforms.
            </p>

            {/* Completion indicator */}
            <div className="mb-6">
                <div className="flex justify-between text-sm text-white/70 mb-1">
                    <span>Completion</span>
                    <span>{getCompletionPercentage()}%</span>
                </div>
                <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                    <div 
                        className="h-full bg-yellow-300 rounded-full"
                        style={{ width: `${getCompletionPercentage()}%` }}
                    ></div>
                </div>
            </div>
            
            {/* Assets grid - updated to 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {requiredAssets.map(asset => {
                    const isUploaded = appAssets && appAssets[asset.id]?.url;
                    const isUploading = uploadingFile === asset.id;
                    
                    return (
                        <div 
                            key={asset.id}
                            className={`
                                bg-black/20 rounded-lg p-4 border-2 transition-all
                                ${isUploaded ? 'border-green-500/50' : 'border-white/10 hover:border-white/30'}
                            `}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="text-white font-medium">{asset.name}</h3>
                                    <p className="text-white/50 text-sm">{asset.description}</p>
                                </div>
                                {isUploaded && (
                                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                )}
                            </div>
                            
                            <div className="flex mt-3">
                                {isUploaded ? (
                                    <div className="flex flex-1 gap-2">
                                        <div className="flex-1 bg-black/30 rounded p-2 flex items-center">
                                            {asset.type.includes('image') ? (
                                                <img 
                                                    src={appAssets[asset.id].url} 
                                                    alt={asset.name} 
                                                    className="h-10 w-auto mx-auto object-contain"
                                                />
                                            ) : (
                                                <div className="text-center text-white/50 text-xs">
                                                    {appAssets[asset.id].filename}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => handleFileSelect(asset.id)} 
                                            className="bg-white/10 text-white/70 hover:bg-white/20 p-2 rounded flex-shrink-0 transition-colors"
                                            title="Replace file"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => handleFileSelect(asset.id)}
                                        disabled={isUploading}
                                        className="flex-1 flex justify-center items-center gap-2 bg-white/10 text-white hover:bg-white/20 py-2 px-4 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isUploading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4" />
                                                Upload {asset.name}
                                            </>
                                        )}
                                    </button>
                                )}
                                <input
                                    ref={fileInputRefs.current[asset.id]}
                                    type="file"
                                    accept={asset.type}
                                    onChange={(e) => handleFileChange(asset.id, e)}
                                    className="hidden"
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {/* Info box */}
            <div className="p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg text-white">
                <div className="flex items-start">
                    <Info className="w-4 h-4 text-blue-300 mr-1.5 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-xs">These assets will be used to build your app for different platforms.</p>
                        <p className="text-xs mt-1">For the best results, please follow the recommended dimensions for each asset (coming soon).</p>
                        <p className="text-xs mt-1">All assets will be saved in your organization's folder for later use.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppAssetsSlide; 